import {
  Checkbox,
  Icon,
  InfiniteTable,
  InfiniteTableColumns,
  InfiniteTableSortBy,
  Stack,
  Tooltip,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { notNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { ConfirmButton } from '../../components/confirm_button';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { ExternalRoutes, Routes } from '../../routes';
import { Vehicle, VehicleStatus, VehicleTracker } from '../entities';
import { isMobileTracker } from '../utils';

import styles from './vehicles_list.module.css';

interface VehicleRow extends Vehicle {
  company: NonNullable<Vehicle['company']>;
  created_at: string | null;
  status: VehicleStatus | null;
  telematicsIntegrations: { telematics_integration_id: string }[];
  vehicleTrackers: Pick<VehicleTracker, 'vehicleTrackerId' | 'trackerType'>[];
}

interface VehicleTableRow extends VehicleRow {
  id: string;
}

export interface VehiclesListDataProps {
  data: {
    vehicles: VehicleRow[];
  };
  company?: {
    company_name?: string | null;
    company_id: string;
  };
  search?: string;
}

interface VehiclesListProps extends VehiclesListDataProps {
  onDeleteVehicles: (vehicleIdsToDelete: string[]) => Promise<void>;
}

interface SelectedVehiclesState {
  vehicles: Record<string, boolean | undefined>;
  isSelectAllEnabled: boolean;
}

const getSortValue = (row: VehicleTableRow, key: keyof VehicleTableRow): string => {
  switch (key) {
    case 'vehicle_id':
      return row.vehicle_id;
    case 'license_plate_number':
      return row.license_plate_number ?? '';
    case 'company':
      return row.company.company_name ?? '';
    case 'created_at':
      return row.created_at ?? '';
    case 'status':
      return row.status?.timestamp ?? '';
    default:
      return '';
  }
};

const sortData = (data: VehicleTableRow[], sortBy: InfiniteTableSortBy<keyof VehicleTableRow>): VehicleTableRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp = aVal.localeCompare(bVal);
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const VehiclesList: React.FC<VehiclesListProps> = (props) => {
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehiclesState>({
    vehicles: {},
    isSelectAllEnabled: false,
  });
  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof VehicleTableRow>>({
    columnId: 'license_plate_number',
    order: 'ASC',
  });
  const selectedCompanyId = props.company?.company_id ?? null;

  const effectiveVehicles = useMemo(() => {
    if (!selectedVehicles.isSelectAllEnabled) {
      return selectedVehicles.vehicles;
    }

    return props.data.vehicles.reduce<Record<string, boolean>>(
      (memo, curr) => ({ ...memo, [curr.vehicle_id]: selectedCompanyId !== null }),
      {},
    );
  }, [selectedVehicles.isSelectAllEnabled, selectedVehicles.vehicles, props.data.vehicles, selectedCompanyId]);

  const vehiclesToDelete = Object.entries(effectiveVehicles)
    .map(([key, value]) => (value === true ? key : undefined))
    .filter(notNil);

  const vehiclesToDeleteCount = vehiclesToDelete.length;
  const isAdmin = useIsAdmin();
  const hasVehiclesEditPermission = useHasScopes([BackofficeScope.companyWriteVehicles]);
  const isPrivilegedUser = isAdmin || hasVehiclesEditPermission;
  const isDeleteButtonDisabled = !isPrivilegedUser || vehiclesToDeleteCount === 0 || props.company === undefined;

  const onSelectAllVehicles = useCallback(() => {
    setSelectedVehicles(({ isSelectAllEnabled }) => {
      const vehiclesToSelect = props.data.vehicles.reduce<Record<string, boolean>>(
        (memo, curr) => ({
          ...memo,
          [curr.vehicle_id]: selectedCompanyId !== null ? !isSelectAllEnabled : false,
        }),
        {},
      );
      return { vehicles: vehiclesToSelect, isSelectAllEnabled: !isSelectAllEnabled };
    });
  }, [props.data.vehicles, selectedCompanyId]);

  const onSelectVehicle = useCallback((vehicleId: string) => {
    setSelectedVehicles(({ vehicles }) => ({
      isSelectAllEnabled: false,
      vehicles: { ...vehicles, [vehicleId]: !(vehicles[vehicleId] ?? false) },
    }));
  }, []);

  const onDeleteVehiclesClick = useCallback(
    async (vehicleIdsToDelete: string[]) => {
      await props.onDeleteVehicles(vehicleIdsToDelete);
      setSelectedVehicles({ isSelectAllEnabled: false, vehicles: {} });
    },
    [props],
  );

  const tableData = useMemo(
    () =>
      sortData(
        props.data.vehicles.map((v) => ({ ...v, id: v.vehicle_id })),
        sortBy,
      ),
    [props.data.vehicles, sortBy],
  );

  const tableColumns: InfiniteTableColumns<VehicleTableRow> = useMemo(
    () => [
      {
        id: 'id',
        width: '4%',
        title: (
          <Checkbox
            label=""
            hideLabel
            onChange={() => onSelectAllVehicles()}
            checked={selectedVehicles.isSelectAllEnabled}
          />
        ),
        cellContent: ({ row }) => {
          const isSelectable = selectedCompanyId !== null;
          return (
            <Checkbox
              label=""
              hideLabel
              disabled={!isSelectable}
              checked={effectiveVehicles[row.vehicle_id] ?? false}
              onChange={() => {
                if (isSelectable) {
                  onSelectVehicle(row.vehicle_id);
                }
              }}
            />
          );
        },
      },
      {
        id: 'vehicle_id',
        title: 'ID',
        width: '8%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={Routes.Vehicle.generatePath({ vehicle_id: row.vehicle_id })}>{row.vehicle_id}</NavLink>
        ),
      },
      {
        id: 'license_plate_number',
        title: 'License plate',
        width: '14%',
        isUserSortable: true,
        cellContent: ({ row }) => {
          const isMobile = row.vehicleTrackers.some(isMobileTracker);
          return (
            <>
              {isMobile && (
                <Tooltip content="Linked to Sixfold mobile app">
                  <Icon name="small-mobile" />
                </Tooltip>
              )}
              <NavLink to={Routes.Vehicle.generatePath({ vehicle_id: row.vehicle_id })}>
                {row.license_plate_number}
              </NavLink>
            </>
          );
        },
      },
      {
        id: 'company',
        title: 'Company',
        width: '22%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={Routes.Company.generatePath({ company_id: row.company.company_id })}>
            {row.company.company_name}
          </NavLink>
        ),
      },
      {
        id: 'created_at',
        title: 'Created at',
        width: '14%',
        isUserSortable: true,
        cellContent: ({ row }) => <FormattedDate date={row.created_at} />,
      },
      {
        id: 'status',
        width: '16%',
        title: (
          <>
            Last status at{' '}
            <Tooltip
              content={
                <ul>
                  <li>
                    <strong style={{ color: 'var(--color-green)' }}>Green</strong> — active (status received within the
                    last 30 minutes)
                  </li>
                  <li>
                    <strong style={{ color: 'var(--color-red)' }}>Red</strong> — inactive (no status for over 30
                    minutes)
                  </li>
                </ul>
              }>
              <Icon name="help" size={16} />
            </Tooltip>
          </>
        ),
        isUserSortable: true,
        cellContent: ({ row }) => <FormattedDate date={row.status?.timestamp ?? null} />,
        cellClassName: (rowIndex: number) => {
          const timeBasedStatus = tableData[rowIndex]?.status?.timeBasedStatus ?? null;
          return classnames('vehicle_status_timestamp', {
            ['vehicle-status-active']: timeBasedStatus === 'ACTIVE',
            ['vehicle-status-inactive']: timeBasedStatus === 'INACTIVE',
          });
        },
      },
      {
        id: 'vehicleTrackers',
        title: 'Tours',
        width: '7%',
        cellContent: ({ row }) => (
          <NavLink
            to={`${Routes.Tours.generatePath({})}?vehicle_id=${row.vehicle_id}&carrier_id=${row.company.company_id}`}>
            Show
          </NavLink>
        ),
      },
      {
        id: 'telematicsIntegrations',
        title: 'Integrations',
        width: '15%',
        cellContent: ({ row }) => {
          if (row.telematicsIntegrations.length === 0) return <>-</>;
          return (
            <>
              {row.telematicsIntegrations.map(({ telematics_integration_id }, idx) => (
                <React.Fragment key={telematics_integration_id}>
                  {idx > 0 && ', '}
                  <NavLink
                    to={Routes.CompanyTelematicsIntegration.generatePath({
                      company_id: row.company.company_id,
                      telematics_integration_id,
                    })}>
                    #{telematics_integration_id}
                  </NavLink>
                </React.Fragment>
              ))}
            </>
          );
        },
      },
    ],
    [
      tableData,
      selectedVehicles.isSelectAllEnabled,
      onSelectAllVehicles,
      selectedCompanyId,
      effectiveVehicles,
      onSelectVehicle,
    ],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  if (props.data.vehicles.length === 0) {
    return (
      <div>
        <span>
          No vehicles
          {props.search !== undefined && <> matching &quot;{props.search}&quot;</>}
          {props.company !== undefined && props.company.company_name !== undefined && (
            <>
              {' for '}
              <NavLink to={Routes.Company.generatePath({ company_id: props.company.company_id })}>
                {props.company.company_name}
              </NavLink>
            </>
          )}
        </span>
        <p>
          <em>This vehicle may have been deleted:</em>{' '}
          <a href={ExternalRoutes.deletedVehiclesDashboard} target="_blank" rel="noreferrer">
            Deleted Vehicles Dashboard
          </a>
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRefCallback}>
      <Stack direction="column" gap="medium">
        <Stack alignItems="center" gap="xsmall">
          <ConfirmButton
            initialButtonColor="red"
            label={vehiclesToDeleteCount === 0 ? 'Delete vehicles' : `Delete ${vehiclesToDeleteCount} vehicles`}
            size="mini"
            disabled={isDeleteButtonDisabled}
            onConfirm={() => onDeleteVehiclesClick(vehiclesToDelete)}
          />
          {isDeleteButtonDisabled && (
            <Tooltip content="Deleting vehicles: A company must be selected, at least one vehicle must be selected, and only vehicles belonging to the company can be selected.">
              <Icon name="help" size={20} />
            </Tooltip>
          )}
        </Stack>

        {scrollParent !== null && (
          <InfiniteTable<VehicleTableRow>
            columns={tableColumns}
            data={tableData}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            scrollParent={scrollParent}
            className={styles.table}
          />
        )}
      </Stack>
    </div>
  );
};
